<template>
  <div>
    <v-sheet elevation="1">
      <v-toolbar dense color="white">
        <v-toolbar-title>{{ screenTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <!-- 登録・編集・参照ダイアログ -->
        <edit-dialog
          v-model="editDialog"
          :loading="loading"
          :readonly="readOnly"
          :validationRules="validationRules"
          :deliveryCas="deliveryCas"
          :emergClss="emergClss"
          :staffOnlyFlgs="staffOnlyFlgs"
          :locales="locales"
          :primaryLocaleCode="primaryLocaleCode"
          :oils="oils"
          :enableAttributesMap="enableAttributesMap"
          :multiLocaleCriteria="multiLocaleCriteria"
          :isNew="isNewEditedItem"
          v-bind.sync="editedItem"
          @save="handleSaveEditDialog"
          @close="handleCloseEditDialog"
        ></edit-dialog>
      </v-toolbar>
      <v-container fluid>
        <!-- メッセージ -->
        <error-message :error-object="errorObject" />
        <success-message :success-object="successObject" />
        <p style="margin: 5px 0 15px 0">
          災害配信定型文マスタは下の表を参照してください。条件を指定することで一覧から絞り込むことができます。
        </p>
        <v-row class="ml-1">
          <v-col cols="3">
            <!-- 検索条件: 配信先キャリア -->
            <v-select
              v-model="deliveryCaCode"
              :items="deliveryCas"
              :placeholder="config.PLACEHOLDER_SELECT"
              label="配信先キャリア"
              item-text="name"
              item-value="code"
              clearable
              outlined
            ></v-select>
          </v-col>
          <v-col cols="3">
            <!-- 検索条件: 緊急事態区分 -->
            <v-select
              v-model="emergClsCode"
              :items="emergClss"
              :placeholder="config.PLACEHOLDER_SELECT"
              label="緊急事態区分"
              item-text="name"
              item-value="code"
              clearable
              outlined
            ></v-select>
          </v-col>
          <v-col cols="3">
            <!-- 検索条件: 用途 -->
            <v-select
              v-model="staffOnlyFlgCode"
              :items="staffOnlyFlgs"
              :placeholder="config.PLACEHOLDER_SELECT"
              label="用途"
              item-text="name"
              item-value="code"
              clearable
              outlined
            ></v-select>
          </v-col>
          <v-spacer></v-spacer>
        </v-row>
        <v-row class="ml-1">
          <v-col cols="12">
            <!-- 検索条件: フリーテキスト -->
            <v-text-field
              v-model="freeText"
              label="フリーテキスト"
              placeholder="テンプレート名称、件名、本文から検索"
              append-icon="mdi-magnify"
              hide-details
              clearable
              outlined
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="ml-4 mt-8" style="font-size: small" no-gutters>
          <!-- 凡例 -->
          <v-col cols="12">【凡例】</v-col>
          <v-col cols="12" class="d-flex">
            <div class="pr-4">配信先キャリア</div>
            <ul class="guide-list pa-0">
              <li v-for="{ code, name } in deliveryCas" :key="code">
                {{ name }}
              </li>
            </ul>
            <div class="pl-8 pr-4">緊急事態区分</div>
            <ul class="guide-list pa-0">
              <li v-for="{ code, name } in emergClss" :key="code">
                {{ name }}
              </li>
            </ul>
            <div class="pl-8 pr-4">用途</div>
            <ul class="guide-list pa-0">
              <li v-for="{ code, alias, name } in staffOnlyFlgs" :key="code">
                {{ alias }}：{{ name }}
              </li>
            </ul>
            <div class="pl-8 pr-4">各言語</div>
            <ul class="guide-list pa-0">
              <li>○：登録あり</li>
              <li>－：登録なし</li>
            </ul>
            <v-spacer></v-spacer>
          </v-col>
        </v-row>
        <v-form>
          <!-- 一覧表 -->
          <top-operation-data-table
            ref="table"
            :headers="tableHeaders"
            :items="filteredItems"
            locale="ja-jp"
            must-sort
          >
            <template v-slot:item="{ item }">
              <tr>
                <!-- テンプレート名称 -->
                <td class="text-start text-truncate text-truncate-force">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">{{
                        item.template_name
                      }}</span>
                    </template>
                    <span>{{ item.template_name }}</span>
                  </v-tooltip>
                </td>
                <!-- 配信先キャリア -->
                <td class="text-start">
                  {{ item.delivery_ca }}
                </td>
                <!-- 緊急事態区分 -->
                <td class="text-start">
                  {{ item.emerg_cls }}
                </td>
                <!-- 用途 -->
                <td class="text-start">
                  {{ getStaffOnlyFlgAlias(item.staff_only_flg) }}
                </td>
                <!-- 件名 -->
                <td class="text-start text-truncate text-truncate-force">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">{{
                        item[primaryLocaleCode].fixed_phrase_title
                      }}</span>
                    </template>
                    <span>{{
                      item[primaryLocaleCode].fixed_phrase_title
                    }}</span>
                  </v-tooltip>
                </td>
                <!-- 本文/避難行動指針 -->
                <td class="text-start text-truncate text-truncate-force">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">{{
                        item[primaryLocaleCode].fixed_phrase
                      }}</span>
                    </template>
                    <span>{{ item[primaryLocaleCode].fixed_phrase }}</span>
                  </v-tooltip>
                </td>
                <!-- 各言語(日本語除く) -->
                <td
                  class="text-start"
                  v-for="{ code: lang } in locales.filter(
                    ({ isPrimary }) => !isPrimary
                  )"
                  :key="lang"
                >
                  {{ lang in item ? "○" : "－" }}
                </td>
                <!-- 操作 -->
                <td class="text-start">
                  <template v-if="readOnly">
                    <!-- 参照ボタン -->
                    <v-btn
                      small
                      class="mx-2"
                      color="primary"
                      @click="handleView(item)"
                      >参照</v-btn
                    >
                  </template>
                  <template v-else>
                    <!-- 編集ボタン -->
                    <v-btn
                      small
                      class="mx-2"
                      color="primary"
                      @click="handleEdit(item)"
                      >編集</v-btn
                    >
                    <!-- 削除ボタン -->
                    <v-btn
                      small
                      class="mx-2"
                      color="primary"
                      @click="handleDelete(item)"
                      >削除</v-btn
                    >
                  </template>
                </td>
              </tr>
            </template>
          </top-operation-data-table>
        </v-form>
      </v-container>
    </v-sheet>
    <!-- ダイアログ -->
    <error-dialog ref="errorDialog" />
    <confirm-dialog ref="confirmDialog" />
    <!-- オーバーレイ -->
    <v-overlay :value="overlay" opacity="1" color="white">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import ConfirmDialog from "@/components/ConfirmDialog";
import config from "@/js/const";
import cloneDeep from "lodash/cloneDeep";
import EditDialog from "@/components/disaster-fixedphrase-mst/EditDialog";
import ErrorDialog from "@/components/ErrorDialog";
import messageUtils from "@/mixins/message-utils";
import TopOperationDataTable from "@/components/ui/TopOperationDataTable";
import { RepositoryFactory } from "@/js/repository/RepositoryFactory";
import { ScreenId } from "@/js/value-objects/screen-id";

const disasterFixedphraseMstRepository = RepositoryFactory.get(
  "disasterFixedphraseMst"
);

// 必須チェックエラー
const ERROR_MSG_REQUIRED = "必須項目です。";
// 桁数チェックエラー
const ERROR_MSG_MAXLENGTH = "文字数が超過しています。";

/**
 * バリデーションルールを作成する。
 */
const getRules = () => ({
  required: () => v => !!v || ERROR_MSG_REQUIRED,
  maxLength: length => v => v.length <= length || ERROR_MSG_MAXLENGTH
});

/**
 * 配信キャリアリストを取得する。
 * @function
 * @return {object[]} 配信キャリアリスト
 */
const getDeliveryCas = () => [
  { code: "PU", name: "ＰＵ：プッシュ通知" },
  { code: "LA", name: "ＬＡ：緊急速報メール" },
  { code: "LI", name: "ＬＩ：Ｌアラート（お知らせ）" },
  { code: "LE", name: "ＬＥ：Ｌアラート（避難情報）" }
];

/**
 * 緊急事態区分リストを取得する。
 * @function
 * @return {object[]} 緊急事態区分リスト
 */
const getEmergClss = () => [
  { code: "NL", name: "ＮＬ：緊急事態無し" },
  { code: "AL", name: "ＡＬ：警戒事態" },
  { code: "SE", name: "ＳＥ：施設敷地緊急事態" },
  { code: "GE", name: "ＧＥ：全面緊急事態" },
  { code: "FN", name: "ＦＮ：終結" }
];

/**
 * 用途リストを取得する。
 * @function
 * @return {object[]} 用途リスト
 */
const getStaffOnlyFlgs = () => [
  { code: "0", alias: "住民", name: "住民広報向け" },
  { code: "1", alias: "防災", name: "防災関係者連絡向け" }
];

/**
 * 言語リストを取得する。
 * @function
 * @return {object[]} 言語リスト
 */
const getLocales = () => [
  { code: "ja", name: "日本語", isPrimary: true },
  { code: "en", name: "英語" },
  { code: "ko", name: "韓国語" },
  { code: "zhcn", name: "中国語（簡体）" },
  { code: "zhtw", name: "中国語（繁体）" }
];

/**
 * OILリストを取得する。
 * @function
 * @return {object[]} OILリスト
 */
const getOils = () => [
  { code: "0", name: "ー", isDefault: true },
  { code: "1", name: "1" },
  { code: "2", name: "2" },
  { code: "3", name: "3" },
  { code: "4", name: "4" },
  { code: "6", name: "6" },
  { code: "9", name: "解除" }
];

/**
 * 一覧表のヘッダーを作成する。
 * @function
 * @returns {object[]} ヘッダー
 */
const createTableHeaders = () => [
  { text: "テンプレート名称", value: "template_name", width: "35%" },
  {
    text: "配信先\nキャリア",
    value: "delivery_ca",
    class: "text-pre",
    width: "7em"
  },
  {
    text: "緊急事態\n区分",
    value: "emerg_cls",
    class: "text-pre",
    width: "7em"
  },
  { text: "用途", value: "staff_only_flg", width: "7em" },
  { text: "件名", value: `ja.fixed_phrase_title`, width: "25%" },
  { text: "本文/避難行動指針", value: `ja.fixed_phrase`, width: "40%" },
  { text: "英語", value: "en", width: "7em", sortable: false },
  { text: "韓国語", value: "ko", width: "7em", sortable: false },
  {
    text: "中国語\n（簡体）",
    value: "zhcn",
    class: "text-pre",
    width: "7em",
    sortable: false
  },
  {
    text: "中国語\n（繁体）",
    value: "zhtw",
    class: "text-pre",
    width: "7em",
    sortable: false
  },
  { text: "操作", value: "actions", width: "14.5em", sortable: false }
];

/**
 * 配信キャリア毎の有効なマスタアイテム(言語)の属性リストを取得する。
 */
const getEnableAttributes = () => ({
  // プッシュ通知
  PU: [
    { name: "fixed_phrase_title", length: 50 },
    { name: "fixed_phrase", label: "本文", length: 1000 }
  ],
  // 緊急速報メール
  LA: [
    { name: "fixed_phrase_title", length: 15 },
    { name: "fixed_phrase", label: "本文", length: 200 }
  ],
  // Ｌアラート（お知らせ）
  LI: [
    { name: "fixed_phrase_title", length: 128 },
    { name: "fixed_phrase", label: "本文", length: 2000 },
    { name: "link_url", length: 1000 }
  ],
  // Ｌアラート（避難情報）
  LE: [
    { name: "fixed_phrase_title", length: 128 },
    { name: "oil" },
    { name: "reason", length: 128 },
    { name: "fixed_phrase", label: "避難行動指針", length: 2000 },
    { name: "suppl", length: 128 }
  ]
});

/**
 * 言語は含まないマスタアイテムを作成する。
 * @function
 * @param {object} [item={}] 初期値を置き換える値が設定されたオブジェクト
 * @param {string} item.fixedphrase_no 定型文番号
 * @param {string} [item.delivery_ca=""] 配信先キャリアコード
 * @param {string} [item.emerg_cls=""] 緊急事態区分コード
 * @param {string} [item.template_name=""] テンプレート名称
 * @param {string} [item.staff_only_flg=""] 用途コード
 * @param {string} item.disp_no 表示順
 * @param {string} item.hash ハッシュ
 * @returns {object} マスタアイテム
 */
const createItemWithoutLocales = ({
  fixedphrase_no = undefined,
  delivery_ca = "",
  emerg_cls = "",
  template_name = "",
  staff_only_flg = "",
  disp_no = undefined,
  hash = undefined
} = {}) => ({
  fixedphrase_no,
  delivery_ca,
  emerg_cls,
  template_name,
  staff_only_flg,
  disp_no,
  hash
});

/**
 * マスタアイテムの言語を作成する。
 * @function
 * @param {object} [locale={}] 初期値を置き換える値が設定されたオブジェクト
 * @param {string} [locale.fixed_phrase_title=""] 定型文タイトル
 * @param {string} [locale.fixed_phrase=""] 定型文
 * @param {string} [locale.link_url=""] 関連するホームページ
 * @param {string} [locale.oil=""] OIL
 * @param {string} [locale.reason=""] 発令解除理由
 * @param {string} [locale.evac_behavior=""] 避難行動指針
 * @param {string} [locale.suppl=""] 補足情報
 * @returns {object} マスタアイテムの言語オブジェクト
 */
const createLocaleOfItem = ({
  fixed_phrase_title = "",
  fixed_phrase = "",
  link_url = "",
  oil = "",
  reason = "",
  evac_behavior = "",
  suppl = ""
} = {}) => ({
  fixed_phrase_title,
  fixed_phrase,
  link_url,
  oil,
  reason,
  evac_behavior,
  suppl
});

/**
 * 新規登録ダイアログ用のアイテムを作成する。
 * @function
 * @returns {object} 全ての属性が初期値に設定されたマスタアイテム
 */
const createEditedItem = () =>
  Object.assign(
    createItemWithoutLocales(),
    ...getLocales().map(({ code }) => ({
      [code]: createLocaleOfItem()
    }))
  );

/**
 * マスタアイテムが多言語設定可能かどうかを判定する。
 * @function
 * @param {object} item マスタアイテム
 * @param {string} item.delivery_ca 配信キャリア
 * @param {string} item.staff_only_flg 用途
 * @returns {boolean} 多言語設定可能な場合true、その他の場合false。
 */
const canSetMultiLocales = ({ delivery_ca, staff_only_flg }) =>
  delivery_ca === "PU" && staff_only_flg === "0";

/**
 * 編集したマスタアイテムの不要な属性を初期値に置き換える。
 * @function
 * @param {object} item 編集ダイアログで編集したマスタアイテム
 * @returns {object} 不要な属性を初期値に置き換えたマスタアイテム
 */
const shrinkItem = item => ({
  ...createItemWithoutLocales(item),
  ...shrinkLocales(item)
});

/**
 * 編集したマスタアイテムの各言語の不要な属性を初期値に置き換える。
 * @function
 * @param {object} locale 編集ダイアログで編集したマスタアイテム
 * @returns {object} 必要な言語のみが定義され、不要な属性を初期値に置き換えたマスタアイテム(言語)
 */
const shrinkLocales = item => {
  const codes = canSetMultiLocales(item)
    ? getLocales().map(({ code }) => code)
    : [getLocales().find(({ isPrimary }) => isPrimary).code];

  return Object.fromEntries(
    codes.map(code => [
      code,
      createLocaleOfItem(shrinkLocale(item, item[code]))
    ])
  );
};

/**
 * 編集したマスタアイテム(言語)の不要な属性を削除する。
 * @function
 * @param {object} item マスタアイテム
 * @param {object} item.delivery_ca 配信キャリア
 * @param {string} locale マスタアイテム(言語)
 * @param {object} [enableAttributes=getEnableAttributes()] 配信キャリア毎の有効なマスタアイテム(言語)の属性名リスト
 * @returns {object} 不要な属性を削除したマスタアイテム(言語)
 */
const shrinkLocale = (
  { delivery_ca },
  locale,
  enableAttributes = getEnableAttributes()
) =>
  filterAttribute(
    locale,
    enableAttributes[delivery_ca].map(({ name }) => name)
  );

/**
 * 指定されたオブジェクトから指定された属性のみを取り出した新しいオブジェクトを作成する
 * @function
 * @param {object} target オブジェクト
 * @param {string[]} attributes 属性名
 * @returns {object} 属性のみを含むオブジェクト
 */
const filterAttribute = (target, attributes) =>
  Object.fromEntries(
    attributes.map(attribute => [attribute, target[attribute]])
  );

/**
 * 必要なら多言語設定可能なマスタアイテムの不足している言語を初期値で追加する。
 * @function
 * @param {object} item 災害配信定型文マスタアイテム
 * @returns 多言語設定可能な場合、全ての言語が存在する災害配信定型文マスタアイテム
 */
const addAllLocaleIfNeeded = item =>
  canSetMultiLocales(item)
    ? Object.assign(
        item,
        ...getLocales()
          .map(({ code }) => code)
          .filter(code => !item[code])
          .map(code => ({ [code]: createLocaleOfItem() }))
      )
    : item;

/**
 * 災害配信定型文マスタのレコードを取得する。
 */
const fetchDisasterFixedphraseMstRecords = async screenId => {
  return (await disasterFixedphraseMstRepository.list(screenId)).data.results;
};

/**
 * Ｌアラート（避難情報）の場合は、避難行動指針を本文にする。
 */
const toEditableRecord = item =>
  Object.assign(
    {},
    item,
    ...getLocales()
      .map(({ code }) => code)
      .filter(code => item[code])
      .map(code => {
        const result = { ...item[code] };

        if (item.delivery_ca === "LE") {
          result.fixed_phrase = result.evac_behavior;
          result.evac_behavior = "";
        }

        return { [code]: result };
      })
  );

/**
 * Ｌアラート（避難情報）の場合は、本文を避難行動指針に戻す。
 */
const fromEditableRecord = item =>
  Object.assign(
    {},
    item,
    ...getLocales()
      .map(({ code }) => code)
      .filter(code => item[code])
      .map(code => {
        const result = { ...item[code] };

        if (item.delivery_ca === "LE") {
          result.evac_behavior = result.fixed_phrase;
          result.fixed_phrase = "";
        }

        return { [code]: result };
      })
  );

/**
 * 登録APIのペイロードを作成する。
 * @function
 * @param {object} item 編集した登録用のマスタアイテム
 * @return {object} ペイロード
 */
const createCreatePayload = item => shrinkItem(item);

/**
 * 更新APIのペイロードを作成する。
 * @function
 * @param {object} item 編集した更新用のマスタアイテム
 * @return {object} ペイロード
 */
const craeteUpdatePayload = item => shrinkItem(item);

/**
 * 削除APIのペイロードを作成する。
 * @function
 * @param {object} item 削除対象のマスタアイテム
 * @param {object} item.fixedphrase_no 削除対象の定型文番号
 * @param {object} item.hash 削除対象のハッシュ
 * @returns {object} ペイロード
 */
const createDeletePayload = ({ fixedphrase_no, hash }) => ({
  fixedphrase_no,
  hash
});

export default {
  mixins: [messageUtils],

  components: {
    ConfirmDialog,
    ErrorDialog,
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage"),
    EditDialog,
    TopOperationDataTable
  },

  data: () => ({
    overlay: false,
    loading: false,
    screenTitle: "",
    screenId: "",
    readOnly: true,
    config,

    // 災害配信定型文マスタレコードリスト
    items: [],
    // 配信先キャリア
    deliveryCaCode: "",
    // 緊急事態区分
    emergClsCode: "",
    // 用途
    staffOnlyFlgCode: "",
    // フリーテキスト
    freeText: "",
    // 編集ダイアログ
    editDialog: false,
    // 編集ダイアログで編集中のレコードのインデックス(新規登録時は-1)
    editedIndex: -1,
    // 編集ダイアログで編集したアイテム
    editedItem: createEditedItem()
  }),

  computed: {
    /**
     * バリデーションルール
     */
    validationRules() {
      return getRules();
    },

    /**
     * 編集ダイアログで編集中のアイテムが新規かどうかを示す値
     */
    isNewEditedItem() {
      return this.editedIndex === -1;
    },

    /**
     * 配信キャリアリスト
     */
    deliveryCas() {
      return getDeliveryCas();
    },

    /**
     * 緊急事態区分リスト
     */
    emergClss() {
      return getEmergClss();
    },

    /**
     * 用途リスト
     */
    staffOnlyFlgs() {
      return getStaffOnlyFlgs();
    },

    /**
     * 言語リスト
     */
    locales() {
      return getLocales();
    },

    /**
     * 主言語（日本語）コード
     */
    primaryLocaleCode() {
      return this.locales.find(locale => locale.isPrimary).code;
    },

    /**
     * OILリスト
     */
    oils() {
      return getOils();
    },

    /**
     * 配信キャリア毎の有効な属性名リスト
     */
    enableAttributesMap() {
      return getEnableAttributes();
    },

    /**
     * 多言語が入力可能かどうかを判定する関数
     */
    multiLocaleCriteria() {
      return canSetMultiLocales;
    },

    /**
     * 用途リストのコードから別名（一覧表示用）を取得するためのオブジェクト
     */
    staffOnlyFlgAliasFrom() {
      return Object.fromEntries(
        this.staffOnlyFlgs.map(elem => [elem.code, elem.alias])
      );
    },

    /**
     * 一覧表のヘッダー
     */
    tableHeaders() {
      return createTableHeaders();
    },

    /**
     * フィルタ済みの一覧表データ
     */
    filteredItems() {
      return this.items
        .filter(
          // 配信先キャリア
          item =>
            !this.deliveryCaCode || item.delivery_ca === this.deliveryCaCode
        )
        .filter(
          // 緊急事態区分
          item => !this.emergClsCode || item.emerg_cls === this.emergClsCode
        )
        .filter(
          // 用途
          item =>
            !this.staffOnlyFlgCode ||
            item.staff_only_flg === this.staffOnlyFlgCode
        )
        .filter(
          // フリーテキスト
          item =>
            !this.freeText ||
            item.template_name?.includes(this.freeText) ||
            item[this.primaryLocaleCode].fixed_phrase_title?.includes(
              this.freeText
            ) ||
            item[this.primaryLocaleCode].fixed_phrase?.includes(this.freeText)
        );
    }
  },

  watch: {
    $route: "initialize",

    /**
     * 配信先キャリアの変更を監視
     */
    deliveryCaCode() {
      this.resetPage();
    },

    /**
     * 緊急事態区分の変更を監視
     */
    emergClsCode() {
      this.resetPage();
    },

    /**
     * 用途の変更を監視
     */
    staffOnlyFlgCode() {
      this.resetPage();
    },

    /**
     * フリーテキストの変更を監視
     */
    freeText() {
      this.resetPage();
    }
  },

  /**
   * ライフサイクルイベント
   */
  created() {
    this.initialize();
  },

  mounted() {
    this.pageTitle(this.screenTitle);
  },

  methods: {
    /**
     * コンポーネントを初期化する。
     */
    async initialize() {
      this.overlay = true;
      try {
        // 操作権限
        const screen = this.$screenOperationAuthority(
          ScreenId.SF3001,
          ScreenId.SF3002
        );
        this.screenId = screen.screenId;
        this.readOnly = screen.readOnly;
        this.screenTitle = screen.screenTitle;

        // 初期データ取得
        await this.refreshRecords();
      } catch (error) {
        this.handleError(error, this.screenId, "aggregation-menu");
      } finally {
        this.overlay = false;
      }
    },

    /**
     * 用途の省略名を取得する。
     * @param {string} flag 用途フラグ
     * @returns 用途の省略名
     */
    getStaffOnlyFlgAlias(flag) {
      return this.staffOnlyFlgAliasFrom[flag] ?? "";
    },

    /**
     * 参照ボタンのクリックイベントを処理する。
     */
    handleView(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = item;
      this.editDialog = true;
    },

    /**
     * 編集ボタンのクリックイベントを処理する。
     */
    handleEdit(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = addAllLocaleIfNeeded(cloneDeep(item));
      this.editDialog = true;
    },

    /**
     * 削除ボタンのクリックイベントを処理する。
     */
    async handleDelete(item) {
      this.clearMessage();

      const messages = [
        "削除した場合には元に戻せません。本当によろしいですか？"
      ];
      for (let message of messages) {
        if (!(await this.showConfirmDialog("削除", message))) {
          return;
        }
      }

      this.overlay = true;
      try {
        // 災害配信定型文マスタを削除して再取得
        this.handleSuccess(await this.deleteItem(item));
        await this.refreshRecords();
      } catch (error) {
        this.handleUpdateError(error);
      } finally {
        this.overlay = false;
      }
    },

    /**
     * 編集ダイアログの登録または更新ボタンクリックイベントを処理する。
     */
    async handleSaveEditDialog() {
      this.clearMessage();

      this.overlay = true;
      this.loading = true;
      try {
        // 災害配信定型文マスタを登録または更新して再取得
        if (this.isNewEditedItem) {
          this.handleSuccess(await this.createRecord());
        } else {
          this.handleSuccess(await this.updateRecord());
        }
        await this.refreshRecords();
      } catch (error) {
        this.handleUpdateError(error);
      } finally {
        this.closeEditDialog();
        this.overlay = false;
        this.loading = false;
      }
    },

    /**
     * 編集ダイアログのキャンセルまたは戻るボタンクリックイベントを処理する。
     */
    handleCloseEditDialog() {
      this.closeEditDialog();
    },

    /**
     * 災害配信定型文マスタを取得する。
     */
    async refreshRecords() {
      this.items = (
        await fetchDisasterFixedphraseMstRecords(this.screenId)
      ).map(toEditableRecord);
    },

    /**
     * 災害配信定型文マスタを登録する。
     */
    async createRecord() {
      return await disasterFixedphraseMstRepository.create(
        this.screenId,
        fromEditableRecord(createCreatePayload(this.editedItem))
      );
    },

    /**
     * 災害配信定型文マスタを更新する。
     */
    async updateRecord() {
      return await disasterFixedphraseMstRepository.update(
        this.screenId,
        fromEditableRecord(craeteUpdatePayload(this.editedItem))
      );
    },

    /**
     * 災害配信定型文マスタを削除する。
     * @param {Object} item 削除するアイテム
     */
    async deleteItem(item) {
      return await disasterFixedphraseMstRepository.delete(
        this.screenId,
        createDeletePayload(item)
      );
    },

    /**
     * 編集ダイアログを閉じる。
     */
    closeEditDialog() {
      this.editDialog = false;

      this.$nextTick(() => {
        this.editedIndex = -1;
        this.editedItem = createEditedItem();
      });
    },

    /**
     * エラーダイアログを表示する。
     * @param {...string} messages メッセージ
     */
    async showErrorDialog(...messages) {
      await this.$refs.errorDialog.open(messages);
    },

    /**
     * 確認ダイアログを表示する。
     * @param {string} buttonText ボタンに表示するテキスト
     * @param {...string} messages メッセージ
     * @returns {boolean} キャンセルされていないかどうかを示す値。
     */
    async showConfirmDialog(buttonText, ...messages) {
      return await this.$refs.confirmDialog.open(messages, buttonText);
    },

    /**
     * 一覧表のページネーションをリセット
     */
    resetPage() {
      this.$refs.table?.resetPage();
    }
  }
};
</script>

<style scoped>
.guide-list {
  list-style: none;
}

.text-truncate-force {
  max-width: 0;
}

.align-right-input >>> input {
  text-align: right;
}
</style>
